import { ref, computed, watch } from 'vue';
import { zhCN } from './zh-CN.js';
import { en } from './en.js';
import { fr } from './fr.js';
import { es } from './es.js';
import { de } from './de.js';
import { pt } from './pt.js';
import { ar } from './ar.js';
import { ja } from './ja.js';

const LOCALE_KEY = 'wc26Locale';

const locales = { 'zh-CN': zhCN, en, fr, es, de, pt, ar, ja };
const rtlLocales = new Set(['ar']);
const DEFAULT_LOCALE = 'zh-CN';

function detectBrowserLocale() {
  try {
    const lang = navigator.language;
    if (lang.startsWith('zh')) return 'zh-CN';
    if (lang.startsWith('fr')) return 'fr';
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('de')) return 'de';
    if (lang.startsWith('pt')) return 'pt';
    if (lang.startsWith('ar')) return 'ar';
    if (lang.startsWith('ja')) return 'ja';
    if (lang.startsWith('en')) return 'en';
  } catch {}
  return DEFAULT_LOCALE;
}

function loadSavedLocale() {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && locales[saved]) return saved;
  } catch {}
  return detectBrowserLocale();
}

const locale = ref(loadSavedLocale());

function saveLocale(value) {
  try {
    localStorage.setItem(LOCALE_KEY, value);
  } catch {}
}

const messages = computed(() => locales[locale.value] || locales[DEFAULT_LOCALE]);
const isRTL = computed(() => rtlLocales.has(locale.value));

function resolve(obj, path) {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return null;
    current = current[part];
  }
  return current;
}

function t(key, params) {
  const value = resolve(messages.value, key);
  if (value == null) {
    const fallbackValue = resolve(locales.en, key) || resolve(locales[DEFAULT_LOCALE], key);
    if (fallbackValue == null) return key;
    return typeof fallbackValue === 'function' ? fallbackValue(params) : applyParams(fallbackValue, params);
  }
  return typeof value === 'function' ? value(params) : applyParams(value, params);
}

function applyParams(text, params) {
  if (!params || typeof text !== 'string') return text;
  return Object.entries(params).reduce(
    (result, [key, val]) => result.replaceAll(`{${key}}`, val),
    text,
  );
}

function switchLocale(code) {
  if (locales[code]) {
    locale.value = code;
    saveLocale(code);
    if (rtlLocales.has(code)) {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  }
}

export function useI18n() {
  return { locale, isRTL, t, switchLocale, locales };
}

// Module-level exports for use in utility files (helpers.js, news.js, etc.)
// These are reactive and work inside Vue's computed/watcher context.
export { locale as globalLocale, t as gt, switchLocale as setLocale, isRTL as globalIsRTL };

function stableIndex(seed, length) {
  const text = String(seed || '');
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % Math.max(1, length);
}

export function gtPick(key, seed, params = {}) {
  const templates = resolve(messages.value, key);
  const fallbackTemplates = resolve(locales.en, key) || resolve(locales[DEFAULT_LOCALE], key);
  const arr = templates || fallbackTemplates;
  if (!Array.isArray(arr) || arr.length === 0) return key;
  const template = arr[stableIndex(seed, arr.length)];
  return Object.entries(params).reduce((text, [key, val]) => text.replaceAll(`{${key}}`, val), template);
}
